import { useEffect, useRef, useState } from 'react'
import { Bell, Check } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { notificationsApi } from '../api/notifications'
import { useNotificationsStore } from '../store/notifications'
import type { Notification } from '../api/notifications'
import { formatDateTime } from '../lib/utils'
import toast from 'react-hot-toast'

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { companyId } = useParams<{ companyId: string }>()
  const { notifications, unreadCount, setNotifications, addNotification, markRead, markAllRead } =
    useNotificationsStore()

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list().then(r => r.data.data),
    refetchInterval: 60000,
  })

  const prevIds = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!data) return
    const incoming = data as Notification[]
    const newOnes = incoming.filter(n => !prevIds.current.has(n.id) && !n.isRead)
    newOnes.forEach(n => {
      addNotification(n)
      toast(n.title, { icon: '🔔' })
    })
    prevIds.current = new Set(incoming.map(n => n.id))
    setNotifications(incoming)
  }, [data, setNotifications, addNotification])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleMarkAll = async () => {
    await notificationsApi.markAllRead()
    markAllRead()
  }

  const handleMarkOne = async (id: string) => {
    await notificationsApi.markRead(id)
    markRead(id)
  }

  const handleNotificationClick = async (n: Notification) => {
    setOpen(false)
    if (!n.isRead) {
      await notificationsApi.markRead(n.id)
      markRead(n.id)
    }
    if (!companyId || !n.refType || !n.refId) return
    if (n.refType === 'calendar') {
      navigate(`/company/${companyId}/calendar?event=${n.refId}`)
      return
    }
    navigate(`/company/${companyId}/${n.refType}s/${n.refId}`)
  }

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)} className="btn-ghost btn p-2 rounded-full relative">
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-xs flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={handleMarkAll} className="text-xs text-primary-600 hover:underline flex items-center gap-1">
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {notifications.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No notifications</p>
            ) : (
              notifications.slice(0, 20).map(n => (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`px-4 py-3 hover:bg-gray-50 ${!n.isRead ? 'bg-blue-50' : ''} ${n.refType && n.refId ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <p className="text-sm font-medium text-gray-900">{n.title}</p>
                  {n.body && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>}
                  <p className="text-xs text-gray-400 mt-1">{formatDateTime(n.createdAt)}</p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
